import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import {
  createClient,
  initClient,
  type MediaDevice,
  type ZoomClient,
} from '@fireflyhealth/zoom-web'
import type { AppointmentRetrieveResponse } from '@fireflyhealth/api'
import { Box } from '@mui/material'

import { logger } from '~/shared/lib/logger'
import { SessionPreCall } from './SessionPreCall'
import { SessionCallV2 } from './SessionCallV2'
import { SessionHeader } from './SessionHeader'

interface SessionProps {
  appointment: AppointmentRetrieveResponse
  sessionName: string
  token: string
  modal: boolean
}

interface SessionContext {
  // Client State
  appointment: AppointmentRetrieveResponse
  client: ZoomClient
  modal: boolean
  screen: 'precall' | 'call'
  sessionName: string
  token: string
  // Hardware State
  activeCamera: MediaDevice | null
  activeMicrophone: MediaDevice | null
  activeSpeaker: MediaDevice | null
  cameras: MediaDevice[]
  isMuted: boolean
  isAudioOn: boolean
  isVideoOn: boolean
  microphones: MediaDevice[]
  speakers: MediaDevice[]
  // DOM Refs
  containerRef: React.RefObject<HTMLDivElement | null>
  // Methods
  setActiveCameraId: (id: string) => void
  setActiveMicrophoneId: (id: string) => void
  setActiveSpeakerId: (id: string) => void
  setCameras: (cameras: MediaDevice[]) => void
  setIsMuted: (isMuted: boolean) => void
  setIsAudioOn: (isAudioOn: boolean) => void
  setIsVideoOn: (isVideoOn: boolean) => void
  setMicrophones: (microphones: MediaDevice[]) => void
  setScreen: (screen: 'precall' | 'call') => void
  setSpeakers: (speakers: MediaDevice[]) => void
}

const ZoomSessionContext = createContext({} as SessionContext)

export const useZoomSession = (): SessionContext => useContext(ZoomSessionContext)

export const SessionProvider: React.FC<SessionProps> = props => {
  /**
   * Client State
   */
  const [screen, setScreen] = useState<'precall' | 'call'>('precall')
  const client = createClient()
  const [isClientInitialized, setIsClientInitialized] = useState(false)
  const [clientError, setClientError] = useState(false)

  /**
   * Hardware State
   */
  const [isMuted, setIsMuted] = useState(false)
  const [isAudioOn, setIsAudioOn] = useState(false)
  const [isVideoOn, setIsVideoOn] = useState(true)
  const [cameras, setCameras] = useState<MediaDevice[]>([])
  const [activeCameraId, setActiveCameraId] = useState<string | null>(null)
  const activeCamera = cameras.find(c => c.deviceId === activeCameraId) ?? null
  const [microphones, setMicrophones] = useState<MediaDevice[]>([])
  const [activeMicrophoneId, setActiveMicrophoneId] = useState<string | null>(null)
  const activeMicrophone = microphones.find(c => c.deviceId === activeMicrophoneId) ?? null
  const [speakers, setSpeakers] = useState<MediaDevice[]>([])
  const [activeSpeakerId, setActiveSpeakerId] = useState<string | null>(null)
  const activeSpeaker = speakers.find(c => c.deviceId === activeSpeakerId) ?? null

  /**
   * DOM Refs
   */
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    initClient(client)
      .then(() => {
        setIsClientInitialized(true)
      })
      .catch(e => {
        logger.warn('Failed to initialize Zoom', e)
        setClientError(true)
      })
  }, [client])

  if (clientError) return <div>Zoom Error</div>

  return isClientInitialized ? (
    <ZoomSessionContext.Provider
      value={{
        // Client State
        appointment: props.appointment,
        client,
        modal: props.modal,
        screen,
        sessionName: props.sessionName,
        token: props.token,
        // Hardware State
        activeCamera,
        activeMicrophone,
        activeSpeaker,
        cameras,
        isMuted,
        isAudioOn,
        isVideoOn,
        microphones,
        speakers,
        // DOM Refs
        containerRef,
        // Methods
        setActiveCameraId,
        setActiveMicrophoneId,
        setActiveSpeakerId,
        setCameras,
        setIsMuted,
        setIsAudioOn,
        setIsVideoOn,
        setMicrophones,
        setScreen,
        setSpeakers,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'white',
        }}
        ref={containerRef}
      >
        <SessionHeader />
        {screen === 'precall' ? <SessionPreCall /> : null}
        {screen === 'call' ? <SessionCallV2 /> : null}
      </Box>
    </ZoomSessionContext.Provider>
  ) : (
    <></>
  )
}
